function isEqual(a, b, seen = new WeakMap()) {
  // 1. Strict equality (fast path)
  if (a === b) return true;

  // 2. Handle NaN (NaN !== NaN)
  if (Number.isNaN(a) && Number.isNaN(b)) return true;

  // 3. Primitives / different types
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
    return false;
  }

  // 4. Circular reference handling
  if (seen.has(a) && seen.get(a) === b) {
    return true;
  }
  seen.set(a, b);

  // 5. Handle Date
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // 6. Handle RegExp
  if (a instanceof RegExp && b instanceof RegExp) {
    return a.source === b.source && a.flags === b.flags;
  }

  // 7. Handle Map
  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) return false;

    for (let [keyA, valA] of a) {
      let found = false;

      for (let [keyB, valB] of b) {
        if (isEqual(keyA, keyB, seen) && isEqual(valA, valB, seen)) {
          found = true;
          break;
        }
      }

      if (!found) return false;
    }

    return true;
  }

  // 8. Handle Set
  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) return false;

    for (let valA of a) {
      let found = false;

      for (let valB of b) {
        if (isEqual(valA, valB, seen)) {
          found = true;
          break;
        }
      }

      if (!found) return false;
    }

    return true;
  }

  // 9. Handle Array
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
      if (!isEqual(a[i], b[i], seen)) return false;
    }

    return true;
  }

  // 10. Different constructors → not equal
  if (a.constructor !== b.constructor) return false;

  // 11. Handle Objects (including deep nesting)
  const keysA = Reflect.ownKeys(a);
  const keysB = Reflect.ownKeys(b);

  if (keysA.length !== keysB.length) return false;

  for (let key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!isEqual(a[key], b[key], seen)) return false;
  }

  return true;
}

function get(object, path, defaultValue) {
  if (object == null) return defaultValue;

  const keys = Array.isArray(path)
    ? path
    : (() => {
      const result = [];
      path.replace(
        /[^.[\]]+|\[(?:([^"'[\]]+)|(["'])(.*?)\2)\]/g,
        (match, number, quote, quotedValue) => {
          result.push(
            quote
              ? quotedValue
              : number !== undefined
                ? number
                : match
          );
        }
      );
      return result;
    })();

  let result = object;

  for (let key of keys) {
    if (result == null) return defaultValue;
    result = result[key];
  }

  return result === undefined ? defaultValue : result;
}

const groupBy = (array = [], key) => {
  if (!Array.isArray(array) || !key) return {};

  return array.reduce((acc, item) => {
    const group = item?.[key];

    if (group == null) return acc;

    (acc[group] ??= []).push(item);

    return acc;
  }, {});
};

function throttle(func, wait = 0, options = {}) {
  let lastCallTime = 0;
  let timeout = null;
  let lastArgs;
  let lastThis;

  const leading = options.leading !== false;
  const trailing = options.trailing !== false;

  function invoke(time) {
    lastCallTime = time;
    func.apply(lastThis, lastArgs);
    lastArgs = lastThis = null;
  }

  function startTimer(remaining) {
    timeout = setTimeout(() => {
      timeout = null;

      if (trailing && lastArgs) {
        invoke(Date.now());
      }
    }, remaining);
  }

  function throttled(...args) {
    const now = Date.now();

    if (!lastCallTime && !leading) {
      lastCallTime = now;
    }

    const remaining = wait - (now - lastCallTime);

    lastArgs = args;
    lastThis = this;

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }

      invoke(now);
    } else if (!timeout && trailing) {
      startTimer(remaining);
    }
  }

  throttled.cancel = function () {
    if (timeout) {
      clearTimeout(timeout);
    }
    lastCallTime = 0;
    timeout = lastArgs = lastThis = null;
  };

  throttled.flush = function () {
    if (timeout && lastArgs) {
      clearTimeout(timeout);
      invoke(Date.now());
      timeout = null;
    }
  };

  return throttled;
}

export { get, groupBy, throttle, isEqual }